import { useEffect, useState, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import Button from '@components/ui/Button'
import Input from '@components/ui/Input'
import Badge from '@components/ui/Badge'
import Modal from '@components/ui/Modal'
import MultiSelectAutocomplete from '@components/ui/MultiSelectAutocomplete'
import {
  Calculator,
  Calendar,
  Users,
  DollarSign,
  Plus,
  Trash2,
  TrendingUp,
  Save,
  Filter,
  ChevronDown,
  ChevronUp,
  Play,
  CheckCircle2,
  CheckCircle,
  ArrowLeft,
  BarChart3,
  HelpCircle,
  Info,
  Wallet
} from 'lucide-react'
import { listRules } from '@services/ruleService'
import { getLatestMonthSummary, getDistincts } from '@services/payrollService'
import { createScenario, getScenario, updateScenario } from '@services/scenarioService'
import { useToast } from '@hooks/useToast'
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts'
import { useFilteredDimensions } from '@hooks/useFilteredDimensions'

const CALCULATION_BASES = [
  { value: 'gross', label: 'Salário Bruto', description: 'Valor total antes dos descontos' },
  { value: 'net', label: 'Salário Líquido', description: 'Valor após descontos' }
]

export default function SimulatorPageNew() {
  const toast = useToast()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const scenarioId = searchParams.get('id')
  const isEditing = !!scenarioId
  
  // Estado do fluxo progressivo
  const [currentStep, setCurrentStep] = useState(1)
  const [calculationBase, setCalculationBase] = useState('gross')
  const [referenceMonth, setReferenceMonth] = useState('')
  
  // Dados base da simulação
  const [baseData, setBaseData] = useState(null) // { total_employees, payroll_total }
  const [loadingBase, setLoadingBase] = useState(false)
  
  // Filtros: separados em pending (editáveis) e applied (aplicados)
  const [pendingFilters, setPendingFilters] = useState({
    grupo: [],
    cargo: [],
    fundo: [],
    regime: [],
    grupo_evento: [],
    evento: [],
    situacao: [],
    secretaria: []
  })
  const [appliedFilters, setAppliedFilters] = useState({
    grupo: [],
    cargo: [],
    fundo: [],
    regime: [],
    grupo_evento: [],
    evento: [],
    situacao: [],
    secretaria: []
  })
  const [showFilters, setShowFilters] = useState(false)
  
  const [loadingDetailed, setLoadingDetailed] = useState(false)

  // Dimensões dinâmicas baseadas nos grupos pendentes (para cascata em tempo real)
  const previewFilters = useMemo(() => ({
    ...appliedFilters,
    grupo: pendingFilters.grupo || []
  }), [appliedFilters, pendingFilters.grupo])

  const { dimensions: previewDims, loading: loadingDims } = useFilteredDimensions(previewFilters, referenceMonth)

  // Regras disponíveis e aplicadas
  const [availableRules, setAvailableRules] = useState([])
  const [appliedRules, setAppliedRules] = useState([]) // { rule, impact, proportion, estimated_increase }
  const [loadingRules, setLoadingRules] = useState(false)
  const [searchTerm, setSearchTerm] = useState('');
  
  // Projeções
  const [projections, setProjections] = useState([])
  const [loadingProjections, setLoadingProjections] = useState(false)
  const [discountRate, setDiscountRate] = useState(0)
  
  // Soma 
  const [simulationResult, setSimulationResult] = useState(null)
  const [runningSimulation, setRunningSimulation] = useState(false)

  // Modal de detalhes da regra
  const [showRuleDetailsModal, setShowRuleDetailsModal] = useState(false)
  const [selectedRuleDetails, setSelectedRuleDetails] = useState(null)
  const [saving, setSaving] = useState(false)
  const [loadingScenario, setLoadingScenario] = useState(false)

  // Modal de salvar cenário
  const [showSaveModal, setShowSaveModal] = useState(false)
  const [scenarioName, setScenarioName] = useState('')
  const [scenarioDescription, setScenarioDescription] = useState('')
  const [loadedScenario, setLoadedScenario] = useState(null)

  const [allAvailableGroups, setAllAvailableGroups] = useState([]);
  const { dimensions: unrestrictedDims } = useFilteredDimensions({}, referenceMonth);

  useEffect(() => {
  if (unrestrictedDims.grupos && unrestrictedDims.grupos.length > 0 && allAvailableGroups.length === 0) {
    setAllAvailableGroups(unrestrictedDims.grupos);
  }
}, [unrestrictedDims.grupos, allAvailableGroups.length]);

const filteredRules = useMemo(() => {
  if (!searchTerm.trim()) return availableRules;

  const term = searchTerm.toLowerCase().trim();

  return availableRules.filter(rule =>
    rule.name.toLowerCase().includes(term) ||
    (rule.description || '').toLowerCase().includes(term)
  );
}, [availableRules, searchTerm]);

  const filteredOptions = useMemo(() => ({
    grupo: previewDims.grupos || [],
    cargo: previewDims.cargos || [],
    fundo: previewDims.fundos || [],
    regime: previewDims.regimes || [],
    grupo_evento: previewDims.grupos_evento || [],
    evento: previewDims.descricoes_evento || [],
    situacao: previewDims.situacoes || [],
    secretaria: (previewDims.secretarias || []).map(s => s.secretaria || '')
  }), [previewDims])

  useEffect(() => {
    const gruposSelecionados = pendingFilters.grupo || []

    if (gruposSelecionados.length === 0) {
      // Se nenhum grupo, limpa tudo
      setPendingFilters(prev => ({
        ...prev,
        cargo: [],
        fundo: [],
        regime: [],
        grupo_evento: [],
        evento: [],
        situacao: [],
        secretaria: []
      }))
      return
    }

    // Usa as dimensões atuais (já filtradas pelos grupos selecionados)
    const limparSeInvalido = (chave, opcoesDisponiveis) => {
      const valoresAtuais = pendingFilters[chave] || []
      const valoresValidos = valoresAtuais.filter(v =>
        Array.isArray(opcoesDisponiveis) ? opcoesDisponiveis.includes(v) : false
      )

      if (valoresValidos.length !== valoresAtuais.length) {
        setPendingFilters(prev => ({ ...prev, [chave]: valoresValidos }))
      }
    }

    limparSeInvalido('cargo', previewDims.cargos || [])
    limparSeInvalido('fundo', previewDims.fundos || [])
    limparSeInvalido('regime', previewDims.regimes || [])
    limparSeInvalido('grupo_evento', previewDims.grupos_evento || [])
    limparSeInvalido('evento', previewDims.descricoes_evento || [])
    limparSeInvalido('situacao', previewDims.situacoes || [])

    const secretariasDisponiveis = (previewDims.secretarias || []).map(s => s.secretaria || '')
    limparSeInvalido('secretaria', secretariasDisponiveis)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingFilters.grupo, previewDims])

  // Carregar mês mais recente automaticamente
  useEffect(() => {
    const abort = new AbortController()
    async function loadLatestMonth() {
      try {
        const summary = await getLatestMonthSummary(abort.signal)
        if (summary.month_year) {
          setReferenceMonth(summary.month_year)
        } else {
          // Fallback para mês atual se não conseguir carregar
          const now = new Date()
          const year = now.getFullYear()
          const month = String(now.getMonth() + 1).padStart(2, '0')
          setReferenceMonth(`${year}-${month}`)
        }
      } catch (e) {
        if (!abort.signal.aborted) {
          console.warn('Erro ao carregar mês mais recente:', e.message)
          // Fallback para mês atual
          const now = new Date()
          const year = now.getFullYear()
          const month = String(now.getMonth() + 1).padStart(2, '0')
          setReferenceMonth(`${year}-${month}`)
        }
      }
    }
    loadLatestMonth()
    return () => abort.abort()
  }, [])

  // Dimensões são carregadas automaticamente pelo hook useFilteredDimensions

  // Carregar regras ativas
  useEffect(() => {
    const abort = new AbortController()
    async function load() {
      try {
        const data = await listRules({ is_active: true }, abort.signal)
        setAvailableRules(data || [])
      } catch (e) {
        if (!abort.signal.aborted) {
          toast.error('Erro ao carregar regras')
        }
      }
    }
    load()
    return () => abort.abort()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Carregar cenário se ID fornecido na URL e regras carregadas
  useEffect(() => {
    const scenarioId = searchParams.get('id')
    if (scenarioId && availableRules.length > 0) {
      loadScenario(scenarioId)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, availableRules])

  // Passo 1: Confirmar base de cálculo e carregar dados
  const handleConfirmBase = async () => {
    if (!referenceMonth) {
      toast.warning('Informe o mês de referência')
      return
    }

    setLoadingBase(true)
    try {
      // Buscar resumo da folha usando o endpoint /eventos/summary/
      const summary = await getLatestMonthSummary(null, {
        month_year: referenceMonth
      })
      
      const payrollTotal = calculationBase === 'gross'
        ? (summary.gross_total || 0)
        : (summary.net_total || 0)

      setBaseData({
        total_employees: summary.employee_count || 0,
        payroll_total: payrollTotal
      })

      // Calcular desconto médio baseado em dados reais
      const grossTotal = summary.gross_total || 0
      const netTotal = summary.net_total || 0
      const calculatedDiscountRate = grossTotal > 0 ? ((grossTotal - netTotal) / grossTotal) : 0
      setDiscountRate(calculatedDiscountRate)
      
      setCurrentStep(2)
      toast.success('Base de cálculo confirmada!')
    } catch (e) {
      toast.error('Erro ao carregar dados: ' + e.message)
    } finally {
      setLoadingBase(false)
    }
  }

  // Aplicar filtros e recalcular base
  const handleApplyFilters = async () => {
    setAppliedFilters(pendingFilters)
    setLoadingBase(true)
    try {
      const filt = {}
      Object.entries(pendingFilters).forEach(([k, arr]) => {
        if (Array.isArray(arr) && arr.length) filt[k] = arr
      })

      const summary = await getLatestMonthSummary(null, {
        month_year: referenceMonth,
        ...filt
      })

      const payrollTotal = calculationBase === 'gross'
        ? (summary.gross_total || 0)
        : (summary.net_total || 0)

      setBaseData({
        total_employees: summary.employee_count || 0,
        payroll_total: payrollTotal
      })

      // Recalcular desconto médio baseado nos dados filtrados
      const grossTotal = summary.gross_total || 0
      const netTotal = summary.net_total || 0
      const calculatedDiscountRate = grossTotal > 0 ? ((grossTotal - netTotal) / grossTotal) : 0
      setDiscountRate(calculatedDiscountRate)
      
      toast.info('Filtros aplicados!')
    } catch (e) {
      toast.error('Erro ao aplicar filtros: ' + e.message)
    } finally {
      setLoadingBase(false)
    }
  }

  // Adicionar regra à simulação e calcular impacto
  const handleAddRule = async (rule) => {
    if (!baseData) {
      toast.warning('Configure a base de cálculo primeiro')
      return
    }

    // Verificar se já foi adicionada
    if (appliedRules.some(r => r.rule.id === rule.id)) {
      toast.warning('Regra já adicionada')
      return
    }

    setLoadingRules(true)
    try {
      // Buscar dados agregados por dimensão para calcular proporção real
      const filt = {}
      Object.entries(appliedFilters).forEach(([k, arr]) => {
        if (Array.isArray(arr) && arr.length) filt[k] = arr
      })

      const summary = await getLatestMonthSummary(null, {
        month_year: referenceMonth,
        ...filt
      })

      // Calcular proporção baseada nas condições da regra
      let proportion = 1.0
      let affectedEmployees = baseData.total_employees
      let affectedPayroll = baseData.payroll_total

      if (rule.conditions_json && rule.conditions_json.length > 0) {
        // Calcular proporção baseada nos grupos/cargos nas condições
        const byGroup = summary.by_group || []
        const byCargo = summary.by_cargo || []

        let totalAffected = 0
        let totalPayrollAffected = 0

        rule.conditions_json.forEach(cond => {
          const dim = cond.dimension?.toLowerCase()
          const val = cond.value

          if (dim === 'grupo') {
            const groupData = byGroup.find(g => g.group === val)
            if (groupData) {
              totalAffected += Number(groupData.employees) || 0
              totalPayrollAffected += Number(calculationBase === 'gross' ? groupData.gross : groupData.net) || 0
            }
          } else if (dim === 'cargo') {
            const cargoData = byCargo.find(c => c.cargo === val)
            if (cargoData) {
              totalAffected += Number(cargoData.employees) || 0
              totalPayrollAffected += Number(calculationBase === 'gross' ? cargoData.gross : cargoData.net) || 0
            }
          }
        })

        if (totalAffected > 0) {
          affectedEmployees = totalAffected
          affectedPayroll = totalPayrollAffected
          proportion = totalAffected / baseData.total_employees
        } else {
          // Se não encontrou dados específicos nas condições,
          // usa 100% da base (que já pode estar filtrada)
          proportion = 1.0
          affectedEmployees = baseData.total_employees
          affectedPayroll = baseData.payroll_total
        }
      }
      // Se a regra NÃO tem condições, afeta 100% da base atual (já filtrada)

      // Calcular aumento baseado no tipo
      let estimatedIncrease = 0
      if (rule.increase_type === 'percentage') {
        estimatedIncrease = Number(affectedPayroll * (Number(rule.increase_value) / 100))
      } else {
        estimatedIncrease = Number(affectedEmployees * Number(rule.increase_value))
      }
      estimatedIncrease = Number(estimatedIncrease) || 0

      const impact = {
        rule,
        proportion: proportion * 100,
        affected_employees: affectedEmployees,
        affected_payroll: affectedPayroll,
        estimated_increase: estimatedIncrease,
        percentage_impact: (estimatedIncrease / baseData.payroll_total) * 100
      }

      setAppliedRules(prev => [...prev, impact])
      toast.success(`Regra "${rule.name}" adicionada!`)
      
      if (currentStep === 2) {
        setCurrentStep(3)
      }
    } catch (e) {
      toast.error('Erro ao calcular impacto: ' + e.message)
    } finally {
      setLoadingRules(false)
    }
  }

  // Remover regra aplicada
  const handleRemoveRule = (ruleId) => {
    setAppliedRules(prev => prev.filter(r => r.rule.id !== ruleId))
    toast.info('Regra removida')
  }

  // Calcular totais da simulação
  const simulationTotals = useMemo(() => {
    if (!baseData) return null

    const totalIncrease = Number(appliedRules.reduce((sum, r) => sum + Number(r.estimated_increase), 0))
    const liquidIncrease = calculationBase === 'gross' ? totalIncrease * (1 - discountRate) : totalIncrease
    const liquidPayroll = calculationBase === 'gross' ? Number(baseData.payroll_total) * (1 - discountRate) : Number(baseData.payroll_total)
    const newPayrollTotal = liquidPayroll + liquidIncrease
    const percentageIncrease = liquidPayroll > 0 ? liquidIncrease / liquidPayroll * 100 : 0

    return {
      current_payroll: liquidPayroll,
      total_increase: liquidIncrease,
      new_payroll: newPayrollTotal,
      percentage_increase: percentageIncrease,
      affected_employees: Math.max(...appliedRules.map(r => Number(r.affected_employees)), 0)
    }
  }, [baseData, appliedRules, discountRate, calculationBase])

  // Mostrar detalhes da regra
  const handleShowRuleDetails = (impact) => {
    setSelectedRuleDetails(impact)
    setShowRuleDetailsModal(true)
  }

  // Salvar cenário
  const handleSaveScenario = async () => {
    if (!scenarioName.trim()) {
      toast.warning('Informe o nome do cenário')
      return
    }

    setSaving(true)
    try {
      // Preparar dados do cenário
      const scenarioData = {
        name: `${scenarioName.trim()} - ${Date.now()}`,
        description: scenarioDescription.trim(),
        calculation_base: calculationBase,
        reference_month: referenceMonth,
        base_salary_reference: Math.round((Number(baseData?.payroll_total) || 0) * 100) / 100,
        total_impact: Math.round((Number(simulationTotals?.total_increase) || 0) * 100) / 100,
        status: 'active',
        layout_json: {
          filters: appliedFilters,
          applied_rules: appliedRules.map(impact => ({
            rule_id: impact.rule.id,
            proportion: impact.proportion,
            affected_employees: impact.affected_employees,
            affected_payroll: impact.affected_payroll,
            estimated_increase: impact.estimated_increase,
            percentage_impact: impact.percentage_impact
          })),
          projections: projections,
          simulation_totals: simulationTotals
        }
      }

      if (isEditing) {
        // Atualizar cenário existente
        await updateScenario(scenarioId, scenarioData)
        toast.success(`Cenário "${scenarioName}" atualizado com sucesso!`)
      } else {
        // Criar novo cenário
        await createScenario(scenarioData)
        toast.success(`Cenário "${scenarioName}" salvo com sucesso!`)
      }

      // Fechar modal e redirecionar para página de cenários
      setShowSaveModal(false)
      setScenarioName('')
      setScenarioDescription('')

      // Redirecionar para a página de cenários
      navigate('/cenarios')
    } catch (e) {
      toast.error('Erro ao salvar cenário: ' + e.message)
    } finally {
      setSaving(false)
    }
  }

  // Carregar cenário
  const loadScenario = async (scenarioId) => {
    setLoadingScenario(true)
    try {
      const scenario = await getScenario(scenarioId)
      setLoadedScenario(scenario)
      setScenarioName(scenario.name)
      setScenarioDescription(scenario.description || '')
      const layout = scenario.layout_json || {}

      // Restaurar configurações
      setCalculationBase(scenario.calculation_base || 'gross')
      setReferenceMonth(scenario.reference_month || '')

      // Restaurar filtros
      const savedFilters = layout.filters || {
        grupo: [],
        cargo: [],
        fundo: [],
        regime: [],
        grupo_evento: [],
        evento: [],
        situacao: [],
        secretaria: []
      }
      setPendingFilters(savedFilters)
      setAppliedFilters(savedFilters)

      // Carregar dados base
      if (scenario.reference_month) {
        // Aplicar filtros salvos ao carregar dados base
        const filt = {}
        Object.entries(layout.filters || {}).forEach(([k, arr]) => {
          if (Array.isArray(arr) && arr.length) filt[k] = arr
        })

        const summary = await getLatestMonthSummary(null, {
          month_year: scenario.reference_month,
          ...filt
        })
        const payrollTotal = scenario.calculation_base === 'gross' 
          ? (summary.gross_total || 0)
          : (summary.net_total || 0)
        setBaseData({
          total_employees: summary.employee_count || 0,
          payroll_total: payrollTotal
        })

        // Calcular desconto médio baseado em dados reais
        const grossTotal = summary.gross_total || 0
        const netTotal = summary.net_total || 0
        const calculatedDiscountRate = grossTotal > 0 ? ((grossTotal - netTotal) / grossTotal) : 0
        setDiscountRate(calculatedDiscountRate)
      }

      // Restaurar regras aplicadas
      const applied = layout.applied_rules || []
      setAppliedRules(applied.map(item => {
        const rule = availableRules.find(r => r.id === item.rule_id)
        if (!rule) return null
        return {
          rule,
          proportion: item.proportion || 0,
          affected_employees: item.affected_employees || 0,
          affected_payroll: item.affected_payroll || 0,
          estimated_increase: item.estimated_increase || 0,
          percentage_impact: item.percentage_impact || 0
        }
      }).filter(Boolean))

      // Restaurar projeções
      setProjections(layout.projections || [])

      // Restaurar totais
      // (simulationTotals será calculado automaticamente via useMemo)

      // Ir para o passo de projeções (última etapa)
      setCurrentStep(4)

      toast.success(`Cenário "${scenario.name}" carregado!`)
    } catch (e) {
      toast.error('Erro ao carregar cenário: ' + e.message)
      // Em caso de erro, voltar para o início
      setCurrentStep(1)
    } finally {
      setLoadingScenario(false)
    }
  }

  // Verificar se tem filtros ativos
  const hasActiveFilters = useMemo(() => {
    return Object.values(pendingFilters).some(arr => arr.length > 0)
  }, [pendingFilters])

  // Contar filtros ativos
  const activeFiltersCount = useMemo(() => {
    return Object.values(pendingFilters).reduce((count, arr) => count + arr.length, 0)
  }, [pendingFilters])

  // Carregar projeções quando entrar no passo 4
  useEffect(() => {
    if (currentStep === 4 && appliedRules.length > 0 && baseData) {
      loadProjections()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, appliedRules, baseData])

  const runSimulation = async () => {
    if (appliedRules.length === 0) {
      toast.warning('Adicione pelo menos uma regra para simular')
      return
    }
    setRunningSimulation(true)
    try {
      const payload = {
        month_year: referenceMonth,
        filters: appliedFilters
      }
      const scenarioIdToUse = scenarioId || loadedScenario?.id
      if (!scenarioIdToUse) {
        toast.error('Cenário não identificado')
        return
      }
      const response = await fetch(`/api/scenarios/${scenarioIdToUse}/run/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
      if (!response.ok) throw new Error('Erro na simulação')
      const data = await response.json()
      setSimulationResult(data)
      toast.success('Valor projetado calculado com sucesso!')
    } catch (err) {
      toast.error('Erro: ' + err.message)
    } finally {
      setRunningSimulation(false)
    }
  }
  //fim soma 

  // Função para preencher buracos no histórico com interpolação
  const fillHistoricalGaps = (historicalData) => {
    // Ordenar dados por data
    const sorted = historicalData.sort((a, b) => new Date(a.month + '-01') - new Date(b.month + '-01'))

    // Preencher buracos usando interpolação linear ou último valor conhecido
    for (let i = 0; i < sorted.length; i++) {
      if (sorted[i].payroll === null || sorted[i].payroll === 0) {
        // Encontrar valores válidos antes e depois
        let prevValue = null
        let nextValue = null

        // Procurar valor anterior válido
        for (let j = i - 1; j >= 0; j--) {
          if (sorted[j].payroll && sorted[j].payroll > 0) {
            prevValue = sorted[j].payroll
            break
          }
        }

        // Procurar valor posterior válido
        for (let j = i + 1; j < sorted.length; j++) {
          if (sorted[j].payroll && sorted[j].payroll > 0) {
            nextValue = sorted[j].payroll
            break
          }
        }

        // Interpolar ou usar último valor conhecido
        if (prevValue !== null && nextValue !== null) {
          // Interpolação linear simples
          sorted[i].payroll = (prevValue + nextValue) / 2
        } else if (prevValue !== null) {
          // Usar último valor conhecido
          sorted[i].payroll = prevValue
        } else if (nextValue !== null) {
          // Usar próximo valor conhecido
          sorted[i].payroll = nextValue
        } else {
          // Sem dados históricos, usar um valor base
          sorted[i].payroll = 100000 // Valor arbitrário para evitar quebras
        }
      }
    }

    return sorted
  }

  // Função para calcular variações sazonais baseadas no histórico
  const calculateSeasonalVariations = (historicalData) => {
    if (!historicalData || historicalData.length < 12) return new Array(12).fill(0)

    // Agrupar dados por mês do ano
    const monthlyData = new Array(12).fill().map(() => ({ values: [], avg: 0 }))

    historicalData.forEach(item => {
      const monthIndex = new Date(item.month + '-01').getMonth()
      if (item.payroll && item.payroll > 0) {
        monthlyData[monthIndex].values.push(item.payroll)
      }
    })

    // Calcular média para cada mês
    monthlyData.forEach(month => {
      if (month.values.length > 0) {
        month.avg = month.values.reduce((a, b) => a + b, 0) / month.values.length
      }
    })

    // Calcular variação sazonal (diferença da média geral)
    const overallAvg = monthlyData.reduce((sum, month) => sum + month.avg, 0) / 12
    const seasonalVariations = monthlyData.map(month => month.avg - overallAvg)

    return seasonalVariations
  }

  // Função para distribuir o aumento total considerando sazonalidade
  const distributeIncreaseSeasonally = (totalIncrease, seasonalVariations, months = 12) => {
    if (!seasonalVariations || seasonalVariations.length !== 12) {
      // Distribuição uniforme se não houver dados sazonais
      return new Array(months).fill(totalIncrease / months)
    }

    // Calcular pesos sazonais (normalizados para soma = 1)
    const totalSeasonal = seasonalVariations.reduce((sum, val) => sum + Math.abs(val), 0)
    const seasonalWeights = seasonalVariations.map(val => Math.abs(val) / totalSeasonal)

    // Distribuir o aumento pelos meses baseado nos pesos sazonais
    const monthlyIncreases = []
    for (let i = 0; i < months; i++) {
      const monthIndex = (new Date().getMonth() + i) % 12 // Próximos meses do ano
      const weight = seasonalWeights[monthIndex] || (1 / 12) // Peso padrão se não disponível
      monthlyIncreases.push(totalIncrease * weight)
    }

    return monthlyIncreases
  }

  // Função para calcular projeções baseadas em dados históricos
  const calculateProjections = (historicalData, months = 12) => {
    if (!historicalData || historicalData.length === 0) return []

    // Filtrar dados válidos e ordenar por data (mais antigo primeiro)
    const validData = historicalData
      .filter(item => item.payroll && item.payroll > 0)
      .sort((a, b) => new Date(a.month + '-01') - new Date(b.month + '-01'))

    if (validData.length < 2) return []

    // O último item é o mês de referência
    const referenceMonthData = validData[validData.length - 1]
    const referenceValue = referenceMonthData.payroll

    // Dados históricos para calcular tendências (excluindo mês referência)
    const historicalOnly = validData.slice(0, -1)

    // Calcular tendência baseada no histórico real
    const growthRates = []
    for (let i = 1; i < historicalOnly.length; i++) {
      const prev = historicalOnly[i-1].payroll
      const curr = historicalOnly[i].payroll
      if (prev > 0) {
        growthRates.push((curr - prev) / prev)
      }
    }

    // Calcular taxa de crescimento média (ou usar 0 se não houver dados suficientes)
    const avgGrowthRate = growthRates.length > 0
      ? growthRates.reduce((a, b) => a + b, 0) / growthRates.length
      : 0

    // Calcular variações sazonais baseadas no histórico
    const seasonalVariations = calculateSeasonalVariations(historicalOnly)

    // Valor do aumento bruto total da simulação
    const totalGrossIncrease = (simulationTotals?.total_increase / (1 - discountRate)) || 0

    // Distribuir o aumento total pelos meses projetados considerando sazonalidade
    const monthlyIncreases = distributeIncreaseSeasonally(totalGrossIncrease, seasonalVariations, months)

    const projections = []

    // Para cada mês de projeção
    for (let i = 0; i < months; i++) {
      // Calcular a data do mês projetado
      const projectedDate = new Date(new Date(referenceMonth + '-01').getFullYear(),
                                    new Date(referenceMonth + '-01').getMonth() + i + 1, 1)
      const monthStr = projectedDate.toISOString().slice(0, 7)

      // Índice do mês no ano (0-11) para variações sazonais
      const monthIndex = projectedDate.getMonth()

      // Aplicar crescimento composto + variação sazonal + aumento da simulação
      const growthFactor = Math.pow(1 + avgGrowthRate, i + 1)
      const baseWithGrowth = referenceValue * growthFactor
      const seasonalAdjustment = seasonalVariations[monthIndex] || 0
      const simulationIncrease = monthlyIncreases[i] || 0

      const projectedValue = baseWithGrowth + seasonalAdjustment + simulationIncrease

      projections.push({
        month: monthStr,
        total_gross: projectedValue,
        adjustment: seasonalAdjustment + simulationIncrease,
        type: 'projection'
      })
    }

    return projections
  }  // Função para carregar projeções
  const loadProjections = async () => {
    setLoadingProjections(true)
    try {
      if (!referenceMonth) {
        throw new Error('Mês de referência não definido')
      }

      const refDate = new Date(referenceMonth + '-01')
      if (isNaN(refDate.getTime())) {
        throw new Error('Mês de referência inválido')
      }

      // Preparar filtros
      const filt = {}
      Object.entries(appliedFilters).forEach(([k, arr]) => {
        if (Array.isArray(arr) && arr.length) filt[k] = arr
      })

      // Buscar dados históricos em paralelo para melhor performance
      // Buscar os 12 meses ANTERIORES ao mês de referência (não incluir o mês de referência aqui)
      const historicalPromises = []
      // Criar data sem problemas de timezone
      const [refYear, refMonth] = referenceMonth.split('-').map(Number)
      for (let i = 12; i >= 1; i--) {
        // Calcular o mês correto usando aritmética de mês/ano
        let targetMonth = refMonth - i
        let targetYear = refYear
        while (targetMonth < 1) {
          targetMonth += 12
          targetYear -= 1
        }
        const monthStr = `${targetYear}-${String(targetMonth).padStart(2, '0')}`
        historicalPromises.push(
          getLatestMonthSummary(null, {
            month_year: monthStr,
            ...filt
          }).then(summary => ({
            month: monthStr,
            payroll: summary.gross_total || 0,
            success: true
          })).catch(() => ({
            month: monthStr,
            payroll: null,
            success: false
          }))
        )
      }

      // Executar todas as chamadas em paralelo
      const historicalResults = await Promise.all(historicalPromises)

      // Buscar dados do mês referência separadamente para calcular desconto e incluir no histórico processado
      const monthData = await getLatestMonthSummary(null, {
        month_year: referenceMonth,
        ...filt
      })
      
      // Adicionar o mês de referência ao histórico para processamento
      historicalResults.push({
        month: referenceMonth,
        payroll: monthData.gross_total || 0,
        success: true
      })

      // Calcular desconto médio baseado em dados reais
      const grossTotal = monthData.gross_total || 0
      const netTotal = monthData.net_total || 0
      const calculatedDiscountRate = grossTotal > 0 ? ((grossTotal - netTotal) / grossTotal) : 0
      setDiscountRate(calculatedDiscountRate)

      // Processar dados históricos - preencher buracos com interpolação
      const processedHistorical = fillHistoricalGaps(historicalResults)

      // Calcular projeções baseadas em dados históricos (começando no mês referência)
      const projectionsData = calculateProjections(processedHistorical, 12)

      // Preparar dados para o gráfico: histórico e projeções conectados
      const chartData = []

      // Calcular variações percentuais mensais do histórico
      const monthlyVariations = []
      for (let i = 1; i < processedHistorical.length; i++) {
        const prevValue = processedHistorical[i - 1].payroll
        const currValue = processedHistorical[i].payroll // 0-11

        if (prevValue > 0 && currValue > 0) {
          const variation = ((currValue - prevValue) / prevValue)
          monthlyVariations.push(variation)
        }
      }

      // Encontrar o mês de referência no histórico processado
      // Garantir que estamos usando o mês de referência correto (não o último do histórico)
      const referenceMonthData = processedHistorical.find(h => h.month === referenceMonth)
      
      if (!referenceMonthData) {
        throw new Error(`Mês de referência ${referenceMonth} não encontrado nos dados históricos`)
      }

      const totalGrossIncrease =
      (discountRate >= 1 ? simulationTotals?.total_increase : (simulationTotals?.total_increase / (1 - discountRate))) || 0
      
      // Garantir que estamos usando o mês de referência correto para criar a data
      // referenceMonth está no formato "YYYY-MM" (ex: "2025-11" para novembro)
      const refMonthDate = new Date(referenceMonth + '-01')
      if (isNaN(refMonthDate.getTime())) {
        throw new Error(`Data do mês de referência ${referenceMonth} é inválida`)
      }
      
      // Verificar se a data está correta (evitar problemas de timezone)
      // O mês em JavaScript é 0-indexed, então novembro = 10
      const expectedMonth = parseInt(referenceMonth.split('-')[1]) - 1
      if (refMonthDate.getMonth() !== expectedMonth) {
        // Ajustar para o mês correto se houver problema de timezone
        refMonthDate.setMonth(expectedMonth)
      }
      
      // Adicionar dados históricos processados (EXCETO o mês de referência)
      // Ordenar por data antes de adicionar para garantir ordem correta
      const historicalOnly = processedHistorical
        .filter(h => h.month !== referenceMonth)
        .sort((a, b) => new Date(a.month + '-01') - new Date(b.month + '-01'))

      historicalOnly.forEach((h) => {
        // Usar a mesma lógica de formatação que usamos para as projeções
        const [year, month] = h.month.split('-').map(Number)
        const monthNum = String(month).padStart(2, '0')
        const monthNamesLocal = {
          '01': 'jan', '02': 'fev', '03': 'mar', '04': 'abr', '05': 'mai', '06': 'jun',
          '07': 'jul', '08': 'ago', '09': 'set', '10': 'out', '11': 'nov', '12': 'dez'
        }
        const monthLabel = `${monthNamesLocal[monthNum]}. de ${year}`

        chartData.push({
          month: monthLabel,
          historical_payroll: h.payroll,
          projected_payroll: null,
          reference_point: null,
          isReference: false
        })
      })
      
      // Adicionar PONTO DE REFERÊNCIA VERDE: Mês base + Aumento
      // Usar o valor do mês de referência encontrado no histórico processado
      const referenceValue = (referenceMonthData.payroll || 0) + totalGrossIncrease
      
      // Criar o label do mês de referência diretamente a partir do referenceMonth
      // para evitar problemas de timezone ou formatação
      // referenceMonth está no formato "YYYY-MM" (ex: "2025-11")
      // refYear e refMonth já foram declarados anteriormente (linha 768)
      const refMonthNum = String(refMonth).padStart(2, '0')
      const monthNames = {
        '01': 'jan', '02': 'fev', '03': 'mar', '04': 'abr', '05': 'mai', '06': 'jun',
        '07': 'jul', '08': 'ago', '09': 'set', '10': 'out', '11': 'nov', '12': 'dez'
      }
      const refMonthLabel = `${monthNames[refMonthNum]}. de ${refYear}`

      chartData.push({
        month: refMonthLabel,
        historical_payroll: null,
        projected_payroll: null,
        reference_point: referenceValue, // Ponto verde destacado
        isReference: true
      })

      // Adicionar projeções dos próximos 12 meses usando variações do histórico
      // As projeções começam do mês SEGUINTE ao mês de referência
      let previousValue = referenceValue

      for (let i = 0; i < 12; i++) {
        // i=0: primeiro mês após referência
        // i=1: segundo mês após referência
        // etc.
        // Calcular o mês correto usando aritmética de mês/ano (evitar problemas de timezone)
        let projectedMonth = refMonth + i + 1
        let projectedYear = refYear
        while (projectedMonth > 12) {
          projectedMonth -= 12
          projectedYear += 1
        }
        const monthNames = {
          1: 'jan', 2: 'fev', 3: 'mar', 4: 'abr', 5: 'mai', 6: 'jun',
          7: 'jul', 8: 'ago', 9: 'set', 10: 'out', 11: 'nov', 12: 'dez'
        }
        const monthLabel = `${monthNames[projectedMonth]}. de ${projectedYear}`

        // Usar a variação do mesmo mês do ano anterior (sazonalidade)
        // Se não houver dados suficientes, usar média das variações
        let variation = 0
        if (monthlyVariations.length > 0) {
          // Tentar pegar a variação do mesmo mês do ciclo anterior
          const sameMonthVariationIndex = monthlyVariations.length - (12 - i)
          if (sameMonthVariationIndex >= 0 && sameMonthVariationIndex < monthlyVariations.length) {
            variation = monthlyVariations[sameMonthVariationIndex]
          } else {
            // Se não tiver, usar a média das últimas variações
            const recentVariations = monthlyVariations.slice(-3)
            variation = recentVariations.length > 0 
              ? recentVariations.reduce((a, b) => a + b, 0) / recentVariations.length 
              : 0
          }
        }

        // Aplicar a variação ao valor anterior
        const projectedValue = previousValue * (1 + variation)

        chartData.push({
          month: monthLabel,
          historical_payroll: null,
          projected_payroll: projectedValue,
          reference_point: null,
          isReference: false
        })

        previousValue = projectedValue
      }

      // Ordenar todos os dados por data antes de definir as projeções
      // Isso garante que o gráfico exiba os meses na ordem correta
      const sortedChartData = chartData.sort((a, b) => {
        // Converter labels de mês (ex: "nov. de 2025") para datas para comparação
        const dateA = new Date(a.month.replace(/(\w{3})\. de (\d{4})/, (match, month, year) => {
          const monthMap = { 'jan': '01', 'fev': '02', 'mar': '03', 'abr': '04', 'mai': '05', 'jun': '06',
                           'jul': '07', 'ago': '08', 'set': '09', 'out': '10', 'nov': '11', 'dez': '12' };
          return `${year}-${monthMap[month.toLowerCase()]}-01`;
        }))
        const dateB = new Date(b.month.replace(/(\w{3})\. de (\d{4})/, (match, month, year) => {
          const monthMap = { 'jan': '01', 'fev': '02', 'mar': '03', 'abr': '04', 'mai': '05', 'jun': '06',
                           'jul': '07', 'ago': '08', 'set': '09', 'out': '10', 'nov': '11', 'dez': '12' };
          return `${year}-${monthMap[month.toLowerCase()]}-01`;
        }))
        return dateA - dateB
      })

      setProjections(sortedChartData)
    } catch (e) {
      toast.error('Erro ao carregar projeções: ' + e.message)
    } finally {
      setLoadingProjections(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-4 md:p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center shadow-lg">
            <Calculator className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-gray-600 dark:text-gray-400">Configure e simule o impacto de regras de aumento na folha</p>
          </div>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-1 sm:gap-2 mt-4 overflow-x-auto pb-2">
          {[
            { num: 1, label: 'Base', icon: Calculator },
            { num: 2, label: 'Filtros', icon: Filter },
            { num: 3, label: 'Regras', icon: TrendingUp },
            { num: 4, label: 'Projeções', icon: BarChart3 },
            { num: 5, label: 'Salvar', icon: Save }
          ].map(({ num, label, icon: Icon }, idx) => (
            <div key={num} className="flex items-center flex-shrink-0">
              <div
                className={`flex items-center gap-1.5 sm:gap-2 px-2 sm:px-4 py-2 rounded-lg transition-all ${
                  currentStep === num
                    ? 'bg-primary text-white shadow-lg scale-105'
                    : currentStep > num
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                }`}
              >
                {currentStep > num ? (
                  <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                ) : (
                  <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                )}
                <span className="text-xs sm:text-sm font-medium whitespace-nowrap">{label}</span>
              </div>
              {idx < 4 && (
                <div className={`w-4 sm:w-8 h-0.5 flex-shrink-0 ${currentStep > num ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`} />
              )}
            </div>
          ))}
        </div>
      </motion.div>

      {/* Loading para edição */}
      {loadingScenario && isEditing ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <span className="ml-2 text-gray-600 dark:text-gray-400">Carregando cenário...</span>
        </div>
      ) : (
        <AnimatePresence mode="sync">
        {currentStep === 1 && (
          <motion.div
            key="step1"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="space-y-4"
          >
            <div className="card bg-white dark:bg-gray-800 shadow-xl">
              <div className="card-body">
                <h2 className="card-title text-xl mb-4 flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-primary" />
                  Passo 1: Configure a Base de Cálculo
                  <div title="Salário Bruto: Os aumentos são aplicados sobre o salário bruto antes dos descontos. O valor líquido mostrado é uma estimativa baseada na taxa média de desconto do mês referência. O valor real será calculado quando os descontos efetivos (INSS, IRRF, etc.) forem aplicados sobre o novo salário bruto.

Salário Líquido: Os aumentos são aplicados diretamente sobre o salário líquido atual. Os valores mostrados são precisos e não necessitam de estimativas, pois trabalham com os valores já descontados.

Dica: Use 'Salário Líquido' para simulações mais precisas, ou 'Salário Bruto' quando quiser simular aumentos salariais que afetam a base de cálculo dos impostos.">
                    <HelpCircle className="w-4 h-4 text-primary cursor-help" />
                  </div>
                </h2>

                {/* Seleção de Base */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    Escolha a base de cálculo:
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {CALCULATION_BASES.map((base) => (
                      <motion.button
                        key={base.value}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setCalculationBase(base.value)}
                        className={`p-6 rounded-xl border-2 transition-all text-left ${
                          calculationBase === base.value
                            ? 'border-primary bg-primary/5 shadow-lg'
                            : 'border-gray-200 dark:border-gray-700 hover:border-primary/50'
                        }`}
                      >
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                            calculationBase === base.value
                              ? 'bg-primary text-white'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                          }`}>
                            <DollarSign className="w-6 h-6" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg mb-1">{base.label}</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{base.description}</p>
                            {calculationBase === base.value && (
                              <Badge variant="success" className="mt-2">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Selecionado
                              </Badge>
                            )}
                          </div>
                        </div>
                      </motion.button>
                    ))}
                  </div>
                </div>

                {/* Mês de Referência */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Mês de Referência:
                  </label>
                  <Input
                    type="month"
                    value={referenceMonth}
                    onChange={(e) => setReferenceMonth(e.target.value)}
                    className="max-w-xs"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Os cálculos serão baseados na folha deste mês
                  </p>
                </div>

                {/* Botão Confirmar */}
                <div className="flex justify-end">
                  <Button
                    onClick={handleConfirmBase}
                    isLoading={loadingBase}
                    size="lg"
                    className="min-w-[200px]"
                  >
                    Confirmar e Avançar
                    <Play className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* PASSO 2: Dados Base e Filtros */}
        {currentStep === 2 && (
          <motion.div
            key="step2"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
          >
            {/* Resumo da Base */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="card bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-xl"
              >
                <div className="card-body">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm mb-1">Base de Cálculo</p>
                      <p className="text-2xl font-bold">
                        {calculationBase === 'gross' ? 'Salário Bruto' : 'Salário Líquido'}
                      </p>
                    </div>
                    <Calculator className="w-10 h-10 text-blue-200" />
                  </div>
                </div>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                className="card bg-gradient-to-br from-green-500 to-green-600 text-white shadow-xl"
              >
                <div className="card-body">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-100 text-sm mb-1">Funcionários</p>
                      <p className="text-3xl font-bold">
                        {baseData?.total_employees?.toLocaleString('pt-BR') || '0'}
                      </p>
                    </div>
                    <Users className="w-10 h-10 text-green-200" />
                  </div>
                </div>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                className="card bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-xl"
              >
                <div className="card-body">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm mb-1">Folha Total</p>
                      <p className="text-2xl font-bold">
                        R$ {baseData?.payroll_total?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0,00'}
                      </p>
                    </div>
                    <DollarSign className="w-10 h-10 text-purple-200" />
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Filtros Opcionais */}
            <div className="card bg-white dark:bg-gray-800 shadow-xl">
              <div className="card-body">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Filter className="w-5 h-5 text-primary" />
                    Filtros da Simulação (Opcional)
                    {hasActiveFilters && (
                      <Badge variant="primary">{activeFiltersCount} ativo(s)</Badge>
                    )}
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    {showFilters ? (
                      <>Ocultar <ChevronUp className="w-4 h-4 ml-1" /></>
                    ) : (
                      <>Mostrar <ChevronDown className="w-4 h-4 ml-1" /></>
                    )}
                  </Button>
                </div>

                <AnimatePresence>
                  {showFilters && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="space-y-3"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {[
                      { key: 'grupo', label: 'Grupo' },
                      { key: 'cargo', label: 'Cargo' },
                      { key: 'fundo', label: 'Fundo' },
                      { key: 'regime', label: 'Regime' },
                      { key: 'grupo_evento', label: 'Grupo Evento' },
                      { key: 'evento', label: 'Evento' },
                      { key: 'situacao', label: 'Situação' },
                      { key: 'secretaria', label: 'Secretaria' }
                    ].map(({ key, label }) => (
                      <div key={key}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-gray-500">{label}</span>
                        </div>
                        <MultiSelectAutocomplete
                          label=""
                          options={
                            key === 'grupo'
                              ? allAvailableGroups
                              : filteredOptions[key] || []
                          }
                          value={pendingFilters[key] || []}
                          onChange={(val) => setPendingFilters(prev => ({ ...prev, [key]: val }))}
                          placeholder={
                            key === 'grupo'
                              ? "Selecione um ou mais grupos"
                              : pendingFilters.grupo?.length === 0
                                ? "Selecione um grupo primeiro"
                                : "Selecione..."
                          }
                          disabled={key !== 'grupo' && (pendingFilters.grupo?.length ?? 0) === 0}
                          isLoading={
                            key === 'grupo'
                              ? loadingDims
                              : loadingDims && (pendingFilters.grupo?.length ?? 0) > 0
                          }
                        />
                      </div>
                    ))}
                    </div>

                      <div className="flex justify-end gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setPendingFilters({
                              grupo: [],
                              cargo: [],
                              fundo: [],
                              regime: [],
                              grupo_evento: [],
                              evento: [],
                              situacao: [],
                              secretaria: []
                            })
                            setAppliedFilters({
                              grupo: [],
                              cargo: [],
                              fundo: [],
                              regime: [],
                              grupo_evento: [],
                              evento: [],
                              situacao: [],
                              secretaria: []
                            })
                          }}
                        >
                          Limpar Filtros
                        </Button>
                        <Button
                          onClick={handleApplyFilters}
                          isLoading={loadingBase}
                        >
                          Aplicar Filtros
                        </Button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Botão Avançar para Regras */}
            {currentStep === 2 && (
              <div className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep(1)}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Voltar
                </Button>
                <Button
                  onClick={() => setCurrentStep(3)}
                  size="lg"
                >
                  Avançar para Regras
                  <Play className="w-5 h-5 ml-2" />
                </Button>
              </div>
            )}
          </motion.div>
        )}

        {/* PASSO 3: Adicionar Regras */}
        {currentStep === 3 && (
          <motion.div
            key="step3"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
          >
            {/* Regras Aplicadas */}
            {appliedRules.length > 0 && (
              <div className="card bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-200 dark:border-green-800 shadow-xl">
                <div className="card-body">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    Regras Aplicadas ({appliedRules.length})
                  </h3>

                  <div className="space-y-3">
                    {appliedRules.map((impact, idx) => (
                      <motion.div
                        key={impact.rule.id}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700"
                      >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant="primary">#{idx + 1}</Badge>
                                <h4 className="font-semibold">{impact.rule?.name || 'Regra não encontrada'}</h4>
                                <Badge variant={impact.rule?.increase_type === 'percentage' ? 'success' : 'info'} className="text-xs">
                                  {impact.rule?.increase_type === 'percentage' ? 'Percentual' : 'Valor Fixo'}
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                                {impact.rule.description}
                              </p>

                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                <div>
                                  <p className="text-gray-500 text-xs">Proporção</p>
                                  <p className="font-semibold">{impact.proportion.toFixed(1)}%</p>
                                </div>
                                <div>
                                  <p className="text-gray-500 text-xs">Funcionários Afetados</p>
                                  <p className="font-semibold">{impact.affected_employees.toLocaleString('pt-BR')}</p>
                                </div>
                                <div>
                                  <p className="text-gray-500 text-xs">Valor Estimado</p>
                                  <p className="font-semibold text-green-600">
                                    +R$ {impact.estimated_increase.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {calculationBase === 'gross' ? 'Bruto estimado' : 'Líquido direto'}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-gray-500 text-xs">Impacto na Folha</p>
                                  <p className="font-semibold text-orange-600">
                                    +{impact.percentage_impact.toFixed(1)}%
                                  </p>
                                </div>
                              </div>
                            </div>

                            <div className="flex flex-col gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleShowRuleDetails(impact)}
                                className="text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                              >
                                <BarChart3 className="w-4 h-4 mr-1" />
                                Detalhes
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveRule(impact.rule.id)}
                                className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Resumo Total */}
                  {simulationTotals && (
                    <div className="mt-4 p-4 bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded-lg border-2 border-purple-300 dark:border-purple-700">
                      <h4 className="font-bold text-lg mb-3 text-purple-900 dark:text-purple-200 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5" />
                        Resumo da Simulação
                      </h4>

                      {/* Informações da Configuração */}
                      <div className="mb-4 p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                        <h5 className="font-semibold text-sm mb-2 text-purple-800 dark:text-purple-300">Configuração da Simulação</h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                          <div className="flex items-center gap-2">
                            <Calculator className="w-4 h-4 text-purple-600" />
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">Base de Cálculo:</span>
                              <p className="font-medium">{calculationBase === 'gross' ? 'Salário Bruto' : 'Salário Líquido'}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-purple-600" />
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">Mês de Referência:</span>
                              <p className="font-medium">{referenceMonth}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4 text-purple-600" />
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">Filtros Ativos:</span>
                              <p className="font-medium">{activeFiltersCount}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">  
                      {/* div com Folha Atual Bruta + Aumento Total Bruto + Aumento Total Bruto*/}
                      <div className="lg:col-span-1 bg-purple-50 dark:bg-purple-900/30 rounded-xl p-6 border-purple-300 dark:border-purple-600 shadow-md">
                        <div className="space-y-6">
                          {/* div da Folha Atual Bruta */}
                          <div className="text-center">
                            <p className="text-xs text-gray-600 dark:text-gray-400 flex items-center justify-center gap-1">
                              Folha Atual Bruto
                              <div title="Valor bruto base da folha de pagamento (antes das deduções)">
                                <HelpCircle className="w-3 h-3 text-gray-400 cursor-help" />
                              </div>
                            </p>
                            <p className="text-xl font-extrabold text-gray-900 dark:text-white mt-2">
                              R$ {(calculationBase === 'gross'
                                ? baseData?.payroll_total
                                : baseData?.payroll_total / (1 - discountRate)
                              ).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                          </div>

                          {/* div do Aumento Total Bruto */}
                          <div className="text-center py-4">
                            <p className="text-xs text-gray-600 dark:text-gray-400">Total Bruto</p>
                            <p className="text-xl font-extrabold text-green-600 mt-2">
                              +R$ {(simulationTotals?.total_increase / (1 - discountRate)).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0,00'}
                            </p>
                          </div>

                          {/* div do Total bruto simulado*/}
                          <div className="text-center pt-4 border-t border-purple-200 dark:border-purple-700">
                            <p className="text-xs text-gray-600 dark:text-gray-400 flex items-center justify-center gap-1">
                              Total bruto simulado
                              <div title="Soma do Folha Atual Bruto + Total Bruto">
                                <HelpCircle className="w-3 h-3 text-gray-400 cursor-help" />
                              </div>
                            </p>
                            <p className="text-2xl font-extrabold text-gray-900 dark:text-white mt-2">
                              R$ {
                                (simulationResult?.simulation_data?.total_gross_with_increase ??
                                simulationResult?.total_gross_with_increase ??
                                (calculationBase === 'gross'
                                  ? (baseData?.payroll_total ?? 0) + (simulationTotals?.total_increase / (1 - discountRate) ?? 0)
                                  : (baseData?.payroll_total ?? 0) / (1 - discountRate) + (simulationTotals?.total_increase ?? 0)
                                )
                                ).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                              }
                            </p>
                          </div>
                        </div>
                      </div>

                        {/*div com Folha Atual Líquido + Aumento Total Líquido*/}
                        <div className="space-y-6">
                          {/* Folha Atual Líquido */}
                          <div className="bg-transparent p-4">
                            <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                              Folha Atual Líquido
                              <div title="Valor líquido base da folha de pagamento (após deduções)">
                                <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                              </div>
                            </p>
                            <p className="text-2xl font-bold mt-3">
                              R$ {(calculationBase === 'gross'
                                ? baseData?.payroll_total * (1 - discountRate)
                                : baseData?.payroll_total
                              ).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                            <p className="text-xs text-gray-500 mt-2">Valor líquido base</p>
                          </div>

                          {/* Aumento Total Líquido */}
                          <div className="bg-transparent p-4">
                            <p className="text-sm text-gray-600 dark:text-gray-400">Total Líquido</p>
                            <p className="text-2xl font-bold text-green-600 mt-3">
                              +R$ {simulationTotals?.total_increase?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0,00'}
                            </p>
                            <p className="text-xs text-gray-500 mt-2">Calculo em valores líquidos</p>
                          </div>
                        </div>

                        {/* Cdiv com % de Desconto + Total de Variação*/}
                        <div className="space-y-6">
                          {/* % de Desconto */}
                          <div className="bg-transparent p-4">
                            <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                              % de Desconto
                              <div title="Percentual de desconto aplicado no salário bruto para gerar o líquido">
                                <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                              </div>
                            </p>
                            <p className="text-2xl font-bold text-blue-600 mt-3">
                              {(discountRate * 100).toFixed(1)}%
                            </p>
                            <p className="text-xs text-gray-500 mt-2">Bruto → Líquido</p>
                          </div>

                          {/* Total de Variação */}
                          <div className="bg-transparent p-4">
                            <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                              Total de Variação
                              <div title="Percentual total de variação que será aplicado na folha">
                                <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                              </div>
                            </p>
                            <p className="text-2xl font-bold text-orange-600 mt-3">
                              +{simulationTotals?.percentage_increase?.toFixed(2) || '0,00'}%
                            </p>
                            <p className="text-xs text-gray-500 mt-2">Variação percentual total</p>
                          </div>
                        </div>
                      </div>

                      {calculationBase === 'net' && (
                        <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                          <h5 className="font-semibold text-sm mb-2 text-green-800 dark:text-green-300 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            Cálculo Direto em Salário Líquido
                          </h5>
                          <p className="text-xs text-green-700 dark:text-green-300">
                            Quando a base de cálculo é <strong>Salário Líquido</strong>, o aumento é aplicado diretamente sobre o valor líquido atual,
                            resultando em valores precisos sem necessidade de estimativas de desconto.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Lista de Regras Disponíveis */}
            <div className="card bg-white dark:bg-gray-800 shadow-xl">
            <div className="card-body">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Plus className="w-5 h-5 text-primary" />
                  Regras Disponíveis
                </h3>

                {/* Campo de busca */}
                <div className="relative w-full sm:w-72 md:w-80">
                  <Input
                    type="search"
                    placeholder="Buscar por nome ou descrição..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-10"
                  />
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => setSearchTerm('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                      title="Limpar busca"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              {availableRules.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  Nenhuma regra ativa disponível
                </div>
              ) : loadingRules ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
                </div>
              ) : filteredRules.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  {searchTerm.trim()
                    ? `Nenhuma regra encontrada para "${searchTerm.trim()}"`
                    : "Nenhuma regra corresponde aos critérios"}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {filteredRules.map((rule) => {
                    const isApplied = appliedRules.some((r) => r.rule.id === rule.id);

                    return (
                      <motion.div
                        key={rule.id}
                        whileHover={{ scale: 1.02 }}
                        className={`border-2 rounded-xl p-4 transition-all ${
                          isApplied
                            ? "border-green-500 bg-green-50 dark:bg-green-900/20 opacity-60"
                            : "border-gray-200 dark:border-gray-700 hover:border-primary"
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-semibold text-sm">{rule.name}</h4>
                          <Badge
                            variant={isApplied ? "success" : "secondary"}
                            className="text-xs"
                          >
                            {rule.increase_type === "percentage"
                              ? `${rule.increase_value}%`
                              : `R$ ${Number(rule.increase_value).toLocaleString("pt-BR", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}`}
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                          {rule.description || "Sem descrição"}
                        </p>
                        <Button
                          size="sm"
                          variant={isApplied ? "outline" : "primary"}
                          onClick={() => !isApplied && handleAddRule(rule)}
                          disabled={isApplied || loadingRules}
                          className="w-full"
                        >
                          {isApplied ? (
                            <>
                              <CheckCircle2 className="w-4 h-4 mr-2 text-green-600" />
                              Aplicada
                            </>
                          ) : (
                            <>
                              <Plus className="w-4 h-4 mr-2" />
                              Adicionar
                            </>
                          )}
                        </Button>
                        {isApplied && (
                          <div
                            className="
                              flex items-center justify-center gap-2 
                              text-red-600 hover:text-red-700 
                              text-sm font-medium 
                              py-2 px-3 rounded-md 
                              bg-red-50 dark:bg-red-950/30 
                              hover:bg-red-100 dark:hover:bg-red-900/40 
                              transition-colors
                              cursor-pointer mt-2
                            "
                            onClick={() => handleRemoveRule(rule.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                            <span>Remover</span>
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

            {/* Botão Ver Projeções */}
            {appliedRules.length > 0 && (
              <div className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep(2)}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Voltar
                </Button>
                <Button
                  onClick={() => setCurrentStep(4)}
                  size="lg"
                >
                  Ver Projeções
                  <BarChart3 className="w-5 h-5 ml-2" />
                </Button>
              </div>
            )}
          </motion.div>
        )}

        {/* PASSO 4: Projeções */}
        {currentStep === 4 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >

            <div className="text-center">
              <BarChart3 className="w-12 h-12 mx-auto text-primary-600 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Histórico e Projeções de 25 Meses (Valores Brutos)
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Histórico bruto dos 12 meses anteriores mais o mês de referência e projeção a partir do mês de referência com o valor bruto aplicado
              </p>
            </div>

            {loadingProjections ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                <span className="ml-2 text-gray-600 dark:text-gray-400">Carregando projeções...</span>
              </div>
            ) : projections.length > 0 ? (
              <>
                {/* Resumo em Destaque */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border-2 border-blue-200 dark:border-blue-800 p-6 mb-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-blue-900 dark:text-blue-200">
                        Resumo das Projeções
                      </h3>
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        Impacto médio mensal baseado nas regras aplicadas
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <BarChart3 className="w-5 h-5 text-blue-600" />
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                          Folha Bruta Atual
                        </span>
                      </div>
                      <div className="text-2xl font-bold text-blue-600">
                        R$ {(calculationBase === 'gross'
                          ? baseData?.payroll_total
                          : baseData?.payroll_total / (1 - discountRate)
                        )?.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) || '0'}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">folha bruta base</p>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="w-5 h-5 text-green-600" />
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                          Total Bruto
                        </span>
                      </div>
                      <div className="text-2xl font-bold text-green-600">
                        +R$ {(simulationTotals?.total_increase / (1 - discountRate)).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">total em valores brutos</p>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-5 h-5 text-orange-600" />
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                          Percentual de Aumento
                        </span>
                      </div>
                      <div className="text-2xl font-bold text-orange-600">
                        +{simulationTotals?.percentage_increase?.toFixed(2) || '0.00'}%
                      </div>
                      <p className="text-xs text-gray-500 mt-1">variação total</p>
                    </div>

                    <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg p-5 shadow-lg text-white">
                      <div className="flex items-center gap-2 mb-2">
                        <Wallet className="w-5 h-5 text-purple-200" />
                        <span className="text-xs font-medium text-purple-100 uppercase tracking-wide">
                          Folha Bruta Projetada
                        </span>
                      </div>
                      <div className="text-3xl font-extrabold text-white">
                        R$ {(() => {
                          const currentGross = calculationBase === 'gross'
                            ? baseData?.payroll_total
                            : baseData?.payroll_total / (1 - discountRate)
                          const increaseBruto = simulationTotals?.total_increase / (1 - discountRate) || 0
                          const totalProjected = (currentGross || 0) + increaseBruto
                          return totalProjected.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
                        })()}
                      </div>
                      <p className="text-xs text-purple-100 mt-1">total após projeção</p>
                    </div>
                  </div>
                </div>

                {/* Gráfico de Projeções */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Histórico e Projeções de 25 Meses (Valores Brutos)
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Histórico bruto dos 12 meses anteriores mais o mês de referência e projeção a partir do mês de referência com o valor bruto aplicado
                    </p>
                  </div>

                  <div className="h-[500px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={projections} margin={{ top: 20, right: 30, left: 10, bottom: 80 }}>
                        <defs>
                          <linearGradient id="colorHistorical" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#6B7280" stopOpacity={0.1}/>
                            <stop offset="95%" stopColor="#6B7280" stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="colorProjected" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#DC2626" stopOpacity={0.1}/>
                            <stop offset="95%" stopColor="#DC2626" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" strokeOpacity={0.5} />
                        <XAxis
                          dataKey="month"
                          tick={(props) => {
                            const { payload, index, ...rest } = props;
                            // Mostrar apenas labels alternados (a cada 2 meses) para evitar sobreposição
                            // Sempre mostrar o mês de referência
                            const isReferenceMonth = projections[index]?.isReference;
                            const shouldShow = index % 2 === 0 || isReferenceMonth;

                            if (!shouldShow) return null;

                            return (
                              <g>
                                <text
                                  {...rest}
                                  fill={isReferenceMonth ? '#10B981' : '#6B7280'}
                                  fontWeight={isReferenceMonth ? 'bold' : 'normal'}
                                  fontSize={10}
                                  dy={10}
                                >
                                  {payload.value}
                                </text>
                              </g>
                            );
                          }}
                          interval={0}
                          angle={-45}
                          textAnchor="end"
                          height={80}
                        />
                        <YAxis
                          domain={[
                            (dataMin) => {
                              const min = Math.floor(dataMin / 1000000) * 1000000;
                              return min - 2000000;
                            },
                            (dataMax) => {
                              const max = Math.ceil(dataMax / 1000000) * 1000000;
                              return max + 2000000;
                            }
                          ]}
                          tick={{ fontSize: 12, fill: '#6B7280' }}
                          tickFormatter={(value) => {
                            if (value >= 1000000) {
                              return `R$ ${(value / 1000000).toFixed(1)}M`
                            } else if (value >= 1000) {
                              return `R$ ${(value / 1000).toFixed(0)}k`
                            }
                            return `R$ ${value}`
                          }}
                          width={90}
                          ticks={(() => {
                            const min = Math.floor((projections.reduce((min, p) => {
                              const val = p.historical_payroll || p.projected_payroll || 0;
                              return val > 0 && val < min ? val : min;
                            }, Infinity)) / 1000000) * 1000000 - 2000000;
                            const max = Math.ceil((projections.reduce((max, p) => {
                              const val = p.historical_payroll || p.projected_payroll || 0;
                              return val > max ? val : max;
                            }, 0)) / 1000000) * 1000000 + 2000000;
                            const step = 2000000; // Intervalo fixo de 2M
                            const ticks = [];
                            for (let i = min; i <= max; i += step) {
                              ticks.push(i);
                            }
                            return ticks;
                          })()}
                        />
                        <Tooltip
                          content={({ active, payload, label }) => {
                            if (active && payload && payload.length) {
                              const currentData = payload[0].payload;
                              const currentValue = currentData.historical_payroll || currentData.projected_payroll || currentData.reference_point;
                              const currentIndex = projections.findIndex(p => p.month === label);

                              // Encontrar o valor do mês anterior
                              let previousValue = null;
                              for (let i = currentIndex - 1; i >= 0; i--) {
                                const prevData = projections[i];
                                const prevValue = prevData.historical_payroll || prevData.projected_payroll || prevData.reference_point;
                                if (prevValue && prevValue > 0) {
                                  previousValue = prevValue;
                                  break;
                                }
                              }

                              // Calcular variação percentual
                              let variation = null;
                              if (previousValue && previousValue > 0) {
                                variation = ((currentValue - previousValue) / previousValue) * 100;
                              }

                              const isProjection = currentData.projected_payroll !== null && currentData.projected_payroll !== undefined;
                              const isReferencePoint = currentData.reference_point !== null && currentData.reference_point !== undefined;

                              return (
                                <div className="bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-xl shadow-2xl p-4 min-w-[280px]">
                                  <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                                    <p className="font-bold text-base text-gray-900 dark:text-white">{label}</p>
                                    {isReferencePoint && (
                                      <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full font-medium">
                                        Mês Base + Aumento
                                      </span>
                                    )}
                                    {isProjection && !isReferencePoint && (
                                      <span className="text-xs px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full font-medium">
                                        Projeção
                                      </span>
                                    )}
                                  </div>
                                  {payload.map((entry, index) => (
                                    <div key={index} className="mb-2">
                                      <div className="flex items-center gap-2 mb-1">
                                        <div
                                          className="w-3 h-3 rounded-full shadow-sm"
                                          style={{ backgroundColor: entry.color }}
                                        />
                                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                          {entry.name}
                                        </span>
                                      </div>
                                      <span className="text-2xl font-bold text-gray-900 dark:text-white block ml-5">
                                        R$ {Number(entry.value).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                      </span>
                                    </div>
                                  ))}
                                  {variation !== null && (
                                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                          vs mês anterior
                                        </span>
                                        <div className="flex items-center gap-1">
                                          <span className={`text-lg font-bold ${variation >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                            {variation >= 0 ? '+' : ''}{variation.toFixed(2)}%
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Legend
                          wrapperStyle={{ paddingTop: '20px' }}
                          iconType="line"
                          formatter={(value) => <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{value}</span>}
                        />
                        <Line
                          type="monotone"
                          dataKey="historical_payroll"
                          stroke="#6B7280"
                          strokeWidth={3}
                          dot={{ r: 5, fill: '#6B7280', strokeWidth: 2, stroke: '#fff' }}
                          activeDot={{ r: 7, strokeWidth: 2 }}
                          name="Histórico Bruto"
                          connectNulls={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="reference_point"
                          stroke="#10B981"
                          strokeWidth={0}
                          dot={{ r: 8, fill: '#10B981', strokeWidth: 3, stroke: '#fff' }}
                          activeDot={{ r: 10, strokeWidth: 3 }}
                          name="Mês Base com Aumento"
                          connectNulls={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="projected_payroll"
                          stroke="#DC2626"
                          strokeWidth={3}
                          strokeDasharray="5 5"
                          dot={{ r: 5, fill: '#DC2626', strokeWidth: 2, stroke: '#fff' }}
                          activeDot={{ r: 7, strokeWidth: 2 }}
                          name="Projeção Bruta"
                          connectNulls={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p className="text-sm text-blue-900 dark:text-blue-100 mb-3">
                      <strong className="flex items-center gap-2 mb-2">
                        <Info className="w-4 h-4" />
                        Como interpretar o gráfico:
                      </strong>
                    </p>
                    <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-2 ml-6 list-disc">
                      <li>A <strong>linha cinza sólida</strong> mostra o histórico bruto dos 12 meses anteriores ao mês base</li>
                      <li>O <strong>ponto verde grande</strong> representa o mês base (novembro) com o total aplicado - este é o ponto de partida das projeções</li>
                      <li>A <strong>linha vermelha tracejada</strong> mostra a projeção dos próximos 12 meses usando as variações sazonais do histórico</li>
                    </ul>
                    <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-700">
                      <p className="text-xs text-blue-800 dark:text-blue-200">
                        <strong>💡 Metodologia das Projeções:</strong> Os valores projetados são calculados aplicando ao mês base (com a projeção) as mesmas variações percentuais
                        observadas no histórico, capturando assim a sazonalidade natural da folha de pagamento (ex: 13º salário, férias, etc.).
                      </p>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <BarChart3 className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Nenhuma projeção disponível
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Aplique algumas regras primeiro para visualizar as projeções
                </p>
              </div>
            )}

            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(3)}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar
              </Button>
              <Button
                onClick={() => setCurrentStep(5)}
                disabled={!projections.length}
                className="flex items-center gap-2"
              >
                Salvar Cenário
                <Save className="w-4 h-4" />
              </Button>
            </div>
          </motion.div>
        )}

        {/* PASSO 5: Salvar Cenário */}
        {currentStep === 5 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            <div className="text-center">
              <Save className="w-12 h-12 mx-auto text-primary-600 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Salvar Cenário
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Salve sua simulação como um cenário para referência futura
              </p>
            </div>

            {/* Resumo Final da Simulação */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Resumo da Simulação
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Configurações */}
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Configurações</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Base de Cálculo:</span>
                      <span className="font-medium">{calculationBase === 'gross' ? 'Salário Bruto' : 'Salário Líquido'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Mês de Referência:</span>
                      <span className="font-medium">{referenceMonth}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Filtros Ativos:</span>
                      <span className="font-medium">{activeFiltersCount}</span>
                    </div>
                  </div>
                </div>

                {/* Impacto */}
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Impacto Calculado</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Funcionários Base:</span>
                      <span className="font-medium">{baseData?.total_employees?.toLocaleString('pt-BR') || '0'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Folha Base:</span>
                      <span className="font-medium">R$ {baseData?.payroll_total?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0,00'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Total:</span>
                      <span className="font-medium text-green-600">
                        +R$ {simulationTotals?.total_increase?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0,00'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Salário Bruto:</span>
                      <span className="font-medium text-purple-600">
                        R$ {simulationTotals?.new_payroll?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0,00'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Variação:</span>
                      <span className="font-medium text-orange-600">
                        +{simulationTotals?.percentage_increase?.toFixed(2) || '0,00'}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Regras Aplicadas */}
              <div className="mt-6">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                  Regras Aplicadas ({appliedRules.length})
                </h4>
                <div className="space-y-2">
                  {appliedRules.map((impact, idx) => (
                    <div key={impact.rule.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Badge variant="primary" className="text-xs">#{idx + 1}</Badge>
                        <div>
                          <p className="font-medium text-sm">{impact.rule?.name || 'Regra não encontrada'}</p>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            {impact.proportion.toFixed(1)}% dos funcionários • 
                            +R$ {impact.estimated_increase.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} 
                            ({impact.rule?.increase_type === 'percentage' ? 'Percentual' : 'Valor Fixo'})
                          </p>
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                            Base: {calculationBase === 'gross' ? 'Salário Bruto' : 'Salário Líquido'} • 
                            {calculationBase === 'gross' ? 'Valor estimado (com desconto aplicado)' : 'Valor direto'}
                          </p>
                        </div>
                      </div>
                      <Badge variant="success" className="text-xs">
                        +{impact.percentage_impact.toFixed(2)}%
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Botões de Ação */}
            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(4)}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar
              </Button>
              <Button
                onClick={() => setShowSaveModal(true)}
                size="lg"
                className="flex items-center gap-2"
              >
                Salvar Cenário
                <Save className="w-4 h-4" />
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      )}

      {/* Modal: Salvar Cenário */}
      <Modal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        title="Salvar Simulação como Cenário"
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Nome do Cenário *
            </label>
            <Input
              value={scenarioName}
              onChange={(e) => setScenarioName(e.target.value)}
              placeholder="Ex: Reajuste Anual 2025"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Descrição (opcional)
            </label>
            <textarea
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 min-h-[100px]"
              value={scenarioDescription}
              onChange={(e) => setScenarioDescription(e.target.value)}
              placeholder="Descreva o objetivo deste cenário..."
            />
          </div>

          {/* Resumo */}
          <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
            <h4 className="font-semibold mb-2">Resumo:</h4>
            <ul className="text-sm space-y-1 text-gray-600 dark:text-gray-400">
              <li className="flex items-center gap-2">
                <Calculator className="w-3 h-3" />
                Base: {calculationBase === 'gross' ? 'Bruto' : 'Líquido'}
              </li>
              <li className="flex items-center gap-2">
                <Calendar className="w-3 h-3" />
                Mês: {referenceMonth}
              </li>
              <li className="flex items-center gap-2">
                <TrendingUp className="w-3 h-3" />
                {appliedRules.length} regra(s) aplicada(s)
              </li>
              {simulationTotals && (
                <li className="font-semibold text-green-600 flex items-center gap-2">
                  <DollarSign className="w-3 h-3" />
                  Impacto: +R$ {simulationTotals.total_increase.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} 
                  ({simulationTotals.percentage_increase.toFixed(2)}%)
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="ghost" onClick={() => setShowSaveModal(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSaveScenario}
            isLoading={saving}
            disabled={!scenarioName.trim()}
          >
            <Save className="w-4 h-4 mr-2" />
            Salvar Cenário
          </Button>
        </div>
      </Modal>

      {/* Modal: Detalhes da Regra */}
      <Modal
        isOpen={showRuleDetailsModal}
        onClose={() => setShowRuleDetailsModal(false)}
        title={`Detalhes da Regra: ${selectedRuleDetails?.rule?.name || ''}`}
        size="lg"
      >
        {selectedRuleDetails && (
          <div className="space-y-6">
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <h4 className="font-semibold mb-2">Informações Gerais</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Tipo de Aumento:</span>
                  <p className="font-medium">
                    {selectedRuleDetails.rule.increase_type === 'percentage' ? 'Percentual' : 'Valor Fixo'}
                  </p>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Valor:</span>
                  <p className="font-medium">
                    {selectedRuleDetails.rule.increase_type === 'percentage' 
                      ? `${selectedRuleDetails.rule.increase_value}%` 
                      : `R$ ${Number(selectedRuleDetails.rule.increase_value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Condições da Regra</h4>
              {selectedRuleDetails.rule.conditions_json && selectedRuleDetails.rule.conditions_json.length > 0 ? (
                <div className="space-y-3">
                  {selectedRuleDetails.rule.conditions_json.map((condition, idx) => (
                    <div key={idx} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="font-medium">Condição #{idx + 1}</h5>
                        <Badge variant="secondary">{condition.dimension}</Badge>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Dimensão:</span>
                          <p className="font-medium capitalize">{condition.dimension}</p>
                        </div>
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Valor:</span>
                          <p className="font-medium">{condition.value}</p>
                        </div>
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Funcionários Afetados:</span>
                          <p className="font-medium text-purple-600">
                            Ver resumo abaixo
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600 dark:text-gray-400">Esta regra não possui condições específicas.</p>
              )}
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <h4 className="font-semibold mb-2 text-blue-900 dark:text-blue-200">Resumo do Impacto</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Total Funcionários:</span>
                  <p className="font-medium">{selectedRuleDetails.affected_employees.toLocaleString('pt-BR')}</p>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Folha Afetada:</span>
                  <p className="font-medium">R$ {selectedRuleDetails.affected_payroll.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Total:</span>
                  <p className="font-medium text-green-600">
                    +R$ {selectedRuleDetails.estimated_increase.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">% na Folha:</span>
                  <p className="font-medium text-orange-600">
                    +{selectedRuleDetails.percentage_impact.toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
